<template>
  <div class="fixed inset-0 z-30 flex flex-col items-center bg-[#0a0f18] font-sans overflow-hidden">
    <canvas ref="canvasRef" class="absolute inset-0 z-0"></canvas>

    <div class="relative z-10 w-full max-w-md bg-[#f4f5f9] h-full flex flex-col shadow-2xl">
      
      <!-- 상단 헤더 -->
      <header class="bg-[#1e2532] text-white p-4 shadow-md flex justify-between items-center z-10">
        <h1 class="text-lg font-bold">Global Meridian Cycle</h1>
        <div class="flex space-x-2">
          <router-link v-if="userInfo.role === 'admin'" to="/admin" class="text-xs font-bold bg-blue-600 px-3 py-1.5 rounded hover:bg-blue-700 transition-colors shadow-sm text-center">
            관리자
          </router-link>
          <button @click="handleLogout" class="text-xs font-bold bg-red-600 px-3 py-1.5 rounded hover:bg-red-700 transition-colors shadow-sm">
            로그아웃
          </button>
        </div>
      </header>

      <!-- 본문 영역 (스크롤) -->
      <div class="p-4 flex-1 overflow-y-auto space-y-4 relative">
        
        <!-- 클릭 이벤트를 추가하고 마우스 커서를 포인터로 변경 -->
        <div v-if="activeNotice" @click="showNoticeModal = true" class="bg-blue-600 rounded-lg p-3 flex items-center shadow-md cursor-pointer hover:bg-blue-700 transition-colors">
          <span class="bg-white text-blue-600 text-[10px] font-black px-2 py-0.5 rounded shrink-0 mr-3">공지</span>
          <p class="text-white text-xs font-bold truncate flex-1">{{ activeNotice.title }}</p>
        </div>
        
        <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div class="flex items-center space-x-2 mb-3">
            <span class="text-xs font-bold bg-gray-100 text-gray-700 px-2 py-1 rounded-full flex items-center">
              🌱 일반 회원
            </span>
            <span :class="['text-xs font-bold px-2 py-1 rounded-full', userInfo.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600']">
              {{ userInfo.isActive ? '활성' : '비활성' }}
            </span>
          </div>
          
          <h2 class="text-xl font-extrabold text-gray-800 mb-1">
            {{ userInfo.name }} <span class="text-sm font-normal text-gray-500">({{ userInfo.email }})</span>
          </h2>
          <p class="text-xs text-gray-600 mb-3">
            소속: {{ userInfo.center || '미지정' }} | 국가: {{ userInfo.country || 'KR' }}
          </p>
          
          <div class="flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-200">
            <span class="text-xs text-gray-500 font-mono truncate mr-2">UID: {{ userInfo.uid }}</span>
            <button @click="copyReferralLink" class="text-xs font-bold bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 transition-colors shrink-0">
              추천 링크 복사 📋
            </button>
          </div>
        </div>

        <!-- 나의 메리디안 싸이클 실적 -->
        <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-200 relative">
          <div class="flex justify-between items-end mb-4 border-b pb-2">
            <h3 class="text-sm font-bold text-gray-800">나의 메리디안 싸이클 실적</h3>
            <span class="text-[10px] text-gray-500">단위: PV & 원</span>
          </div>

          <div class="grid grid-cols-3 gap-2 mb-4">
            <div @click="openDetailModal('pv')" class="bg-gray-50 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors group">
              <p class="text-[10px] text-gray-500 font-semibold mb-1">본인 기준 실적 (3배수 기준)</p>
              <p class="text-base font-extrabold text-gray-800">{{ formatNumber(userInfo.personalPV) }}</p>
            </div>
            <div class="bg-yellow-50 p-3 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors group">
              <p class="text-[10px] text-yellow-700 font-semibold mb-1">보유 포인트(P)</p>
              <p class="text-base font-extrabold text-yellow-800">{{ formatNumber(userInfo.points) }}</p>
            </div>
            <div @click="openDetailModal('total_bonus')" class="bg-blue-50 p-3 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors group">
              <p class="text-[10px] text-blue-600 font-semibold mb-1">총 수당 (원)</p>
              <p class="text-base font-extrabold text-blue-700">{{ formatNumber(userInfo.totalEarnedBonus) }}</p>
            </div>
          </div>

          <!-- 세부 보너스 항목 -->
          <div class="space-y-2">
            <div 
              v-for="(amount, label) in bonusList" 
              :key="label" 
              @click="openDetailModal('specific_bonus', label)"
              class="flex justify-between items-center text-sm p-3 bg-gray-50 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <span class="text-gray-600 font-semibold text-xs flex items-center">
                {{ label }}
              </span>
              <span class="font-bold text-gray-800 flex items-center">
                {{ formatNumber(amount) }}
                <svg class="w-3 h-3 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
              </span>
            </div>
          </div>

          <!-- 출금 안내사항 -->
          <div class="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
            <p class="text-[11px] text-red-600 font-bold leading-relaxed">
              * 전산 보너스 지급 시 반드시 [출금 신청]을 해야 다음 날 입금 처리됩니다.<br/>
              * 출금 시 관리자 설정에 따라 5%의 수수료가 공제됩니다.
            </p>
          </div>
        </div>
      </div>

      <!-- 하단 필수 메뉴 아이콘 그리드 -->
      <div class="border-t border-gray-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shrink-0 z-10">
        <div class="grid grid-cols-4 gap-2">
          
          <router-link to="/package-purchase" class="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-blue-50 transition group">
            <div class="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-2 group-hover:bg-blue-600 group-hover:text-white transition shadow-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
            </div>
            <span class="text-[11px] font-bold text-gray-700 text-center">패키지<br/>구매</span>
          </router-link>

          <router-link to="/wallet" class="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-green-50 transition group">
            <div class="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2 group-hover:bg-green-600 group-hover:text-white transition shadow-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
            </div>
            <span class="text-[11px] font-bold text-gray-700 text-center">지갑/<br/>출금</span>
          </router-link>

          <router-link to="/tree" class="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-purple-50 transition group">
            <div class="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-2 group-hover:bg-purple-600 group-hover:text-white transition shadow-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
            </div>
            <span class="text-[11px] font-bold text-gray-700 text-center">조직도/<br/>계보도</span>
          </router-link>

          <router-link to="/profile" class="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition group">
            <div class="w-12 h-12 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center mb-2 group-hover:bg-gray-600 group-hover:text-white transition shadow-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
            </div>
            <span class="text-[11px] font-bold text-gray-700 text-center">정보<br/>수정</span>
          </router-link>
          
        </div>
</div>
    </div>

    <!-- [새로 추가] 공지사항 상세 모달창 -->
    <div v-if="showNoticeModal && activeNotice" class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 transition-opacity px-4">
      <div class="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        <div class="bg-blue-600 p-4 flex justify-between items-center">
          <h3 class="text-white font-bold text-sm flex items-center">
            <span class="bg-white text-blue-600 text-[10px] font-black px-2 py-0.5 rounded mr-2">공지</span>
            안내사항
          </h3>
          <button @click="showNoticeModal = false" class="text-white hover:text-gray-200 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
        <div class="p-6">
          <h4 class="text-lg font-extrabold text-gray-800 mb-3">{{ activeNotice.title }}</h4>
          <p class="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed mb-6">{{ activeNotice.content }}</p>
          <div class="text-right">
            <button @click="showNoticeModal = false" class="bg-gray-800 text-white font-bold py-2 px-6 rounded-lg text-sm hover:bg-black transition-colors shadow-md">닫기</button>
          </div>
        </div>
      </div>
    </div>
    <!-- [새로 추가 끝] -->

    <!-- [상세 내역 확인 모달창] -->
    <div v-if="showDetailModal" class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black bg-opacity-60 transition-opacity">
      <div class="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md h-[70vh] sm:h-auto sm:max-h-[80vh] flex flex-col overflow-hidden shadow-2xl transform transition-transform duration-300">
        
        <!-- 모달 헤더 -->
        <div class="bg-[#1e2532] p-4 flex justify-between items-center shrink-0">
          <h3 class="text-white font-bold">{{ modalTitle }} 상세 내역</h3>
          <button @click="closeDetailModal" class="text-gray-300 hover:text-white transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
        
        <!-- 내역 리스트 (스크롤) -->
        <div class="p-4 overflow-y-auto flex-1 bg-gray-50">
          <div v-if="isModalLoading" class="text-center py-10">
            <p class="text-sm font-bold text-blue-600 animate-pulse">내역을 불러오는 중...</p>
          </div>
          
          <div v-else-if="modalDataList.length === 0" class="text-center py-10">
            <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>
            <p class="text-sm text-gray-500 font-bold">표시할 상세 내역이 없습니다.</p>
          </div>

          <div v-else class="space-y-3">
            <div v-for="item in modalDataList" :key="item.id" class="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              
              <div class="flex justify-between items-start mb-2">
                <div>
                  <span v-if="item.type === 'pv'" class="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded">본인 구매</span>
                  <span v-else class="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{{ item.bonusType }} 보너스</span>
                  
                  <p class="text-xs text-gray-800 font-bold mt-1.5">{{ item.description }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-black" :class="item.type === 'pv' ? 'text-indigo-600' : 'text-green-600'">
                    +{{ formatNumber(item.amount) }} {{ item.type === 'pv' ? 'PV' : '원' }}
                  </p>
                </div>
              </div>

              <div class="pt-2 mt-2 border-t border-gray-100 text-[10px] text-gray-500 flex justify-between">
                <span>{{ formatDate(item.createdAt) }}</span>
                <span v-if="item.sourceName">발생: {{ item.sourceName }}</span>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
    <!-- 모달 끝 -->

  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, limit, getDocs, orderBy } from 'firebase/firestore'; 
import router from '../../router';

export default {
  name: 'MemberDashboard',
  setup() {
    const userInfo = ref({
      uid: '', name: '', email: '', role: '', center: '', country: 'KR', isActive: false, personalPV: 0, totalEarnedBonus: 0, wallet: {}
    });
    
    const canvasRef = ref(null);
    let ctx = null; let animationFrameId = null; const particles = []; const particleCount = 60;

    // 모달 관련 상태
    const showDetailModal = ref(false);
    const isModalLoading = ref(false);
    const modalTitle = ref('');
    const modalDataList = ref([]);

    const loadUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            
            // 🛑 [추가] 접속 중 계정이 정지된 경우 즉시 쫓아내기
            if (data.isActive === false) {
              alert('계정 이용이 정지되었습니다. 자동 로그아웃됩니다.');
              await signOut(auth);
              router.push('/login');
              return;
            }

            userInfo.value = {
              uid: user.uid,
              name: data.name || '회원',
              // ... 기존 로직 유지 ...
              email: user.email,
              role: data.role || 'member',
              center: data.center || '서울본사',
              country: data.country || 'KR',
              isActive: data.isActive !== false,
              personalPV: data.personalPV || 0,
              points: data.points || 0, // 추가됨
              totalEarnedBonus: data.totalEarnedBonus || 0,
              wallet: data.wallet || {}
            };
          }
        } catch (error) {
          console.error("사용자 정보 로드 실패:", error);
        }
      }
    };

    const activeNotice = ref(null);
    const showNoticeModal = ref(false); // [추가] 공지사항 모달 상태 변수

    const fetchActiveNotice = async () => {
      try {
        const q = query(collection(db, 'notices'), where('isActive', '==', true), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          // [수정] 제목(title) 문자열만 가져오지 않고, 전체 데이터(객체)를 가져옴
          activeNotice.value = snap.docs[0].data(); 
        }
      } catch (error) {
        console.error("공지사항 로드 실패:", error);
      }
    };

    const bonusList = computed(() => {
      const w = userInfo.value.wallet;
      return {
        '판매 보너스 (3일째 지급)': w['판매'] || 0,
        '매칭 보너스 (3일째 지급)': w['매칭'] || 0,
        '대수 보너스 (3일째 지급)': w['대수'] || 0,
        '싸이클 보너스 (주마감/수요일)': w['싸이클'] || 0,
        '스타 보너스 (월 2회 마감)': w['스타'] || 0,
        '영업소 관리비 (주마감/수요일)': w['영업소관리'] || 0,
        '영업소 소개비 (주마감/수요일)': w['영업소소개'] || 0,
      };
    });

    const formatNumber = (num) => new Intl.NumberFormat('ko-KR').format(num || 0);

    const formatDate = (timestamp) => {
      if (!timestamp) return '-';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    // 모달창 오픈 및 데이터 로드 함수
    const openDetailModal = async (type, label = '') => {
      showDetailModal.value = true;
      isModalLoading.value = true;
      modalDataList.value = [];

      const user = auth.currentUser;
      if (!user) return;

      try {
        let q;
        const resultList = [];

        // 캐싱용 유저 이름 조회 함수
        const userCache = {};
        const getUserName = async (uid) => {
          if (!uid || uid === 'MASTER') return '-';
          if (userCache[uid]) return userCache[uid];
          const uSnap = await getDoc(doc(db, 'users', uid));
          if (uSnap.exists()) {
            userCache[uid] = uSnap.data().name;
            return userCache[uid];
          }
          return '알수없음';
        };

        if (type === 'pv') {
          modalTitle.value = '누적 PV';
          // 본인의 구매 내역(승인완료된 내역) 조회
          q = query(collection(db, 'purchase_requests'), where('uid', '==', user.uid), where('status', '==', 'APPROVED'), orderBy('approvedAt', 'desc'));
          const snap = await getDocs(q);
          snap.forEach(d => {
            const data = d.data();
            resultList.push({
              id: d.id,
              type: 'pv',
              amount: data.totalPv || data.unitPv,
              description: `${data.level} 패키지 구매 적립`,
              createdAt: data.approvedAt,
              sourceName: userInfo.value.name
            });
          });

        } else if (type === 'total_bonus' || type === 'specific_bonus') {
          
          if (type === 'total_bonus') modalTitle.value = '총 수당 (누적)';
          else modalTitle.value = label.split(' ')[0]; // 예: '판매 보너스 (3일째 지급)' -> '판매'

          // 보너스 내역 조회
          q = query(collection(db, 'bonus_history'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'));
          const snap = await getDocs(q);
          
          for (const d of snap.docs) {
            const data = d.data();
            // 특정 보너스를 클릭한 경우 해당 타입만 필터링
            if (type === 'specific_bonus') {
              const shortType = label.split(' ')[0]; // '판매', '매칭' 등
              if (data.bonusType !== shortType) continue;
            }

            const sourceName = await getUserName(data.sourceUid);
            resultList.push({
              id: d.id,
              type: 'bonus',
              bonusType: data.bonusType,
              amount: data.amount,
              description: data.description,
              createdAt: data.createdAt,
              sourceName: sourceName
            });
          }
        }

        modalDataList.value = resultList;

      } catch (error) {
        console.error("상세 내역 로드 중 오류:", error);
      } finally {
        isModalLoading.value = false;
      }
    };

    const closeDetailModal = () => {
      showDetailModal.value = false;
      modalDataList.value = [];
    };

    const copyReferralLink = () => {
      const link = `${window.location.origin}/signup?ref=${userInfo.value.uid}`;
      navigator.clipboard.writeText(link).then(() => {
        alert('추천 링크가 복사되었습니다.\n' + link);
      }).catch(err => {
        console.error('복사 실패:', err);
      });
    };

    const handleLogout = async () => {
      try {
        await signOut(auth);
        router.push('/login');
      } catch (error) {
        console.error("로그아웃 실패:", error);
      }
    };

    const initCanvas = () => {
      if (!canvasRef.value) return; ctx = canvasRef.value.getContext('2d'); resizeCanvas(); window.addEventListener('resize', resizeCanvas);
      for (let i = 0; i < particleCount; i++) { particles.push({ x: Math.random() * canvasRef.value.width, y: Math.random() * canvasRef.value.height, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4, radius: Math.random() * 1.2 + 0.5 }); }
      animate();
    };
    const resizeCanvas = () => { if (!canvasRef.value) return; canvasRef.value.width = window.innerWidth; canvasRef.value.height = window.innerHeight; };
    const animate = () => {
      if (!canvasRef.value || !ctx) return; ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
      for (let i = 0; i < particles.length; i++) {
        let p = particles[i]; p.x += p.vx; p.y += p.vy; if (p.x < 0 || p.x > canvasRef.value.width) p.vx *= -1; if (p.y < 0 || p.y > canvasRef.value.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2); ctx.fillStyle = 'rgba(255, 204, 0, 0.4)'; ctx.fill();
        for (let j = i + 1; j < particles.length; j++) { let p2 = particles[j]; let dx = p.x - p2.x; let dy = p.y - p2.y; let dist = Math.sqrt(dx * dx + dy * dy); if (dist < 100) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p2.x, p2.y); ctx.strokeStyle = `rgba(255, 204, 0, ${0.8 - dist / 100})`; ctx.lineWidth = 0.5; ctx.stroke(); } }
      } animationFrameId = requestAnimationFrame(animate);
    };

    onMounted(() => { loadUserData(); fetchActiveNotice(); initCanvas(); });
    onBeforeUnmount(() => { window.removeEventListener('resize', resizeCanvas); if (animationFrameId) cancelAnimationFrame(animationFrameId); });

return { 
      userInfo, bonusList, formatNumber, formatDate, copyReferralLink, handleLogout, canvasRef, 
      activeNotice, showNoticeModal, // [수정] showNoticeModal 반환 추가
      showDetailModal, isModalLoading, modalTitle, modalDataList, openDetailModal, closeDetailModal
    };	
  }
}
</script>